import { useCallback, useEffect, useRef, useState, type ChangeEvent, type Dispatch, type SetStateAction, type TextareaHTMLAttributes } from 'react'
import { InputFrame, type InputFrameProps } from '../layout/InputFrame'
import { useDebounce } from '@/hooks/useDebounce'
import { cn } from '@/utilities/misc'

export type TextAreaProps =
  Omit<InputFrameProps, 'prefix' | 'suffix' | 'children'> &
  TextareaHTMLAttributes<HTMLTextAreaElement> &
  {
    autoHeight?: boolean
    monospace?: boolean
    setValue?: Dispatch<SetStateAction<string>>,
    frameClassName?: string
  }

export function TextArea({
  label,
  tooltip,
  tooltipPlacement,
  required,
  disabled,
  width = '100%',
  height,
  autoHeight,
  monospace,
  className,
  frameClassName,
  children,
  onChange,
  setValue,
  value,
  ...rest
}: TextAreaProps) {

  const [observedWidth, setObservedWidth] = useState<number | null>(null)

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const mirrorRef = useRef<HTMLDivElement>(null)
  const debouncedWidth = useDebounce(observedWidth, 300)

  //--------------------------------------------------------------------------------
  const maxHeightPx = (() => {
    if (!autoHeight || height == null) return null
    if (typeof height === 'number') return height
    const parsed = parseFloat(String(height))
    return Number.isFinite(parsed) ? parsed : null
  })()

  //--------------------------------------------------------------------------------
  className = cn(
    'TextArea',
    monospace && 'Monospace',
    autoHeight && 'AutoHeight',
    className,
  )

  //--------------------------------------------------------------------------------
  const adjustHeight = useCallback(() => {
    if (!autoHeight || !inputRef.current || !mirrorRef.current) return
    const el = inputRef.current
    const mirror = mirrorRef.current
    const style = getComputedStyle(el)

    // Match every style that affects wrapping/height so the mirror tracks the
    // textarea without collapsing it (which would scroll the caret into view).
    mirror.style.boxSizing = 'border-box'
    mirror.style.width = `${el.offsetWidth}px`
    mirror.style.padding = style.padding
    mirror.style.borderStyle = style.borderStyle
    mirror.style.borderWidth = style.borderWidth
    mirror.style.font = style.font
    mirror.style.fontSize = style.fontSize
    mirror.style.fontFamily = style.fontFamily
    mirror.style.fontWeight = style.fontWeight
    mirror.style.fontVariantLigatures = style.fontVariantLigatures
    mirror.style.letterSpacing = style.letterSpacing
    mirror.style.wordSpacing = style.wordSpacing
    mirror.style.textRendering = style.textRendering
    mirror.style.lineHeight = style.lineHeight
    mirror.style.whiteSpace = style.whiteSpace || 'pre-wrap'
    mirror.style.overflowWrap = style.overflowWrap
    mirror.style.wordBreak = style.wordBreak
    mirror.style.tabSize = style.tabSize

    let text = value !== undefined ? String(value) : el.value
    // Empty content collapses the mirror; a space keeps one line + padding
    if (!text) text = ' '
    // Trailing newline is ignored by the mirror unless it has a following character
    else if (text.endsWith('\n')) text += '.'
    mirror.textContent = text

    let next = Math.ceil(mirror.scrollHeight)
    const capped = maxHeightPx != null && next > maxHeightPx
    if (capped) next = maxHeightPx
    el.style.overflowY = capped ? 'auto' : 'hidden'
    if (Math.round(el.offsetHeight) === next) return
    el.style.height = `${next}px`
  }, [autoHeight, maxHeightPx, value])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    adjustHeight()
  }, [adjustHeight, value])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    if (!autoHeight || !inputRef.current) return
    const el = inputRef.current
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setObservedWidth(entry.contentRect.width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [autoHeight])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    if (debouncedWidth !== null) adjustHeight()
  }, [debouncedWidth, adjustHeight])

  //--------------------------------------------------------------------------------
  const handleChange = useCallback((ev: ChangeEvent<HTMLTextAreaElement>) => {
    if (onChange)
      onChange(ev)
    if (setValue)
      setValue(ev.target.value)
    adjustHeight()
  }, [setValue, onChange, adjustHeight])

  //--------------------------------------------------------------------------------
  return (
    <InputFrame
      label={label}
      tooltip={tooltip}
      disabled={disabled}
      tooltipPlacement={tooltipPlacement}
      required={required}
      width={width}
      height={autoHeight ? undefined : height}
      className={cn('TextArea', frameClassName)}
    >
      <div style={autoHeight ? { position: 'relative' } : undefined}>
        {autoHeight && (
          <div
            ref={mirrorRef}
            aria-hidden='true'
            className='HiddenMirror'
          />
        )}
        <textarea
          ref={inputRef}
          className={className}
          disabled={disabled}
          value={value}
          onChange={handleChange}
          {...rest}
        />
      </div>
    </InputFrame>
  )
}
